import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@mui/material';
import { get, set } from '@totalsoft/react-state-lens';
import { TextField } from '@totalsoft/rocket-ui';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { valueOrDefault } from 'utils/functions';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { DictionaryDetailTableCell, DictionaryDetailTableRow } from './Styles';
import { required, shape } from '@totalsoft/pure-validations';

const validator = shape({ code: required, name: required });

const DictionaryDetailItemComponent = ({ withDictionaryItem, lens, validation, dirtyInfo, disabled }) => {
  const isNew = lens.isNew |> get;

  const isSystem = lens.isSystem |> get;

  // #region Validation

  /* The details array can be huge so you don't want to validate it entirely on every change.
     But you do want to validate the entire array on save, so use a local validation here, but also
     take into account the "global" validation (passed as prop to this component). The global validation 
     overrides the local one for fields that are not dirty. */

  const [localValidation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    let internalDirtyInfo = { code: false, name: false };
    if (dirtyInfo && typeof dirtyInfo !== 'boolean') internalDirtyInfo = { ...internalDirtyInfo, ...dirtyInfo };
    validate(lens |> get, internalDirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const getValid = useCallback(
    prop => {
      if (!dirtyInfo || typeof dirtyInfo === 'boolean' || !dirtyInfo[prop]) {
        return isValid(validation[prop]);
      }
      return isValid(localValidation[prop]);
    },
    [dirtyInfo, validation, localValidation]
  );

  const getError = useCallback(
    prop => {
      if (!dirtyInfo || typeof dirtyInfo === 'boolean' || !dirtyInfo[prop]) {
        return getErrors(validation[prop]);
      }
      return getErrors(localValidation[prop]);
    },
    [dirtyInfo, validation, localValidation]
  );

  // #endregion

  return (
    <DictionaryDetailTableRow hover={!isNew} isNew={isNew}>
      <DictionaryDetailTableCell>
        <TextField
          fullWidth
          value={lens.code |> get |> valueOrDefault('')}
          disabled={disabled || isSystem}
          onChange={lens.code |> set}
          error={!getValid('code')}
          helperText={getError('code')}
        />
      </DictionaryDetailTableCell>
      <DictionaryDetailTableCell>
        <TextField
          fullWidth
          value={lens.name |> get |> valueOrDefault('')}
          disabled={disabled || isSystem}
          onChange={lens.name |> set}
          error={!getValid('name')}
          helperText={getError('name')}
        />
      </DictionaryDetailTableCell>
      {!withDictionaryItem && (
        <>
          <DictionaryDetailTableCell>
            <TextField
              fullWidth
              value={lens.description |> get |> valueOrDefault('')}
              disabled={disabled || isSystem}
              onChange={lens.description |> set}
            />
          </DictionaryDetailTableCell>
          <DictionaryDetailTableCell>
            <TextField
              fullWidth
              value={lens.orderNo |> get |> valueOrDefault('')}
              disabled={disabled || isSystem}
              decimalScale={0}
              isNumeric={true}
              onChange={lens.orderNo |> set}
            />
          </DictionaryDetailTableCell>
          <DictionaryDetailTableCell align='center'>
            <Checkbox
              checked={lens.implicit |> get |> valueOrDefault(false)}
              disabled={disabled || isSystem}
              onChange={lens.implicit |> set |> onCheckBoxChange}
            />
          </DictionaryDetailTableCell>
        </>
      )}
      <DictionaryDetailTableCell align='center'>
        <Checkbox
          checked={lens.isActive |> get |> valueOrDefault(false)}
          disabled={disabled || isSystem}
          onChange={lens.isActive |> set |> onCheckBoxChange}
        />
      </DictionaryDetailTableCell>
    </DictionaryDetailTableRow>
  );
};

DictionaryDetailItemComponent.propTypes = {
  withDictionaryItem: PropTypes.bool,
  lens: PropTypes.object,
  validation: PropTypes.object,
  dirtyInfo: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  disabled: PropTypes.bool
};

export default DictionaryDetailItemComponent;
